import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { HOAI2021 } from './hoai-data/HOAI2021';
import { HOAI2013 } from './hoai-data/HOAI2013';
import { HOAI2009 } from './hoai-data/HOAI2009';
import ExoForm from '../ExoForm';
import TypeCustomSelect from '../../form/form-fields/TypeCustomSelect';
import { useTranslation } from 'react-i18next';
import MoneyLabel from '../../label/MoneyLabel';
import { useDebounce } from 'use-debounce';
import ExoDialog from '../ExoDialog';
import { CostAllocator } from '../Din276/CostAllocator';
import TypeText from '../../form/form-fields/TypeText';
import {
  API_GET,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../../api/api';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { HOAILoader } from './HOAILoader';
import { tokens } from '../../../global/theme/tokens';
import InfoIcon from '@mui/icons-material/Info';
import FormFields from '../../form/FormFields';

export const HOAICalculator = ({
  startDataCollection,
  noControls,
  onClose,
  onSubmit,
  hoaiTitle,
  debug = false,
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const logResults = debug;

  const hoais = [HOAI2021, HOAI2013, HOAI2009]; //In case of new HOAI import it and add it to this array
  //sort in reverse order (newest should have 0 array index)
  //HOAI INDEX DURCH JAHR ERSETZEN
  const formRef = useRef(null);
  const [dataCollection, setDataCollection] = useState({});
  const [startData, setStartData] = useState({});
  const [tooLowWarning, setTooLowWarning] = useState(false);
  const [phasesOver100, setPhasesOver100] = useState(false);
  const [openDIN, setOpenDIN] = useState(false);
  const [titleCollection, setTitleCollection] = useState({
    title: hoaiTitle ?? '',
  });

  // event debounce
  const [debounceData, setDebounceData] = useState(null);
  const [debounceCalc] = useDebounce(debounceData, 400);

  function getHoaiIndexByYear(year) {
    if (!year) return null;
    return hoais.findIndex((el) => el.year == year);
  }

  const [hoai, setHoai] = useState(
    startDataCollection && Object.keys(startDataCollection).length > 1
      ? hoais[
          getHoaiIndexByYear(
            startDataCollection.hoaiData?.year ?? startDataCollection.year,
          )
        ]
      : hoais[0],
  );
  const [selectedLabel, setSelectedLabel] = useState(
    startDataCollection && Object.keys(startDataCollection).length > 1
      ? (startDataCollection.hoaiData?.labelIndex ?? 0)
      : 0,
  );

  const [openLoad, setOpenLoad] = useState(false);

  useEffect(() => {
    if (
      startDataCollection &&
      Object.keys(startDataCollection).length > 1 &&
      startDataCollection.positions?.length > 0
    ) {
      // Load from existing HOAI data with API positions
      const mapped = mapResponseToDataCollection(startDataCollection);
      const hoaiIndex = getHoaiIndexByYear(mapped.year);

      setTitleCollection({
        title: startDataCollection.hoaiTitle ?? startDataCollection.title,
      });
      setSelectedLabel(mapped.labelIndex);
      setHoai(hoais[hoaiIndex]);

      const staticPhases = hoais[hoaiIndex].data[mapped.labelIndex].phases;
      const parsedPhases = parsePhasesFromPositions(
        startDataCollection.positions,
        staticPhases,
      );

      const initData = {
        ...mapped,
        id: startDataCollection.id,
        hoaiIndex: hoaiIndex,
        phases: parsedPhases || [],
      };

      setStartData(initData);
      updateFields(mapped.labelIndex, hoaiIndex, initData);
      calculateBaseHonorarium(initData);
      return;
    } else if (config && startDataCollection && startDataCollection.id) {
      // Fetch full HOAI from API to get positions
      DBRequest({
        config,
        path: `hoai/${startDataCollection.id}`,
        method: API_GET,
        onResponse: setLoadedHOAI,
      });
      return;
    }

    const defaultData = {
      vat: startDataCollection?.defaultTaxRate ?? 19,
      hoaiIndex: 0,
      labelIndex: selectedLabel,
      honorariumZone: 1,
      honorariumRate: 0,
      year: hoais[0].year,
    };
    setStartData(defaultData);
    setDataCollection(defaultData);
  }, [startDataCollection, config]);

  const additionalCostField = {
    label: t('Additional Costs'),
    type: 'expandFields',
    colspan: 2,
    className: 'flex flex-col w-full md:grid md:grid-cols-2 gap-4',
    fields: [
      /*{
        colspan: 2,
        type: 'iconField',
        icon: InfoIcon,
        iconSize: 'medium',
        iconColor: '',
        tooltip:
          'Zuschlag enthält den Umbau- oder Modernisierungszuschlag nach §6 Abschnitt 5 und andere sonstige Zuschläge.',
        field: {
          label: t('Surcharge') + ' in %',
          key: 'surcharge',
          type: 'number',
        },
      },*/
      {
        label: t('Surcharge') + ' in %',
        key: 'surcharge',
        type: 'number',
        colspan: 2,
        Icon: InfoIcon,
        iconTooltip:
          'Zuschlag enthält den Umbau- oder Modernisierungszuschlag nach §6 Abschnitt 5 und andere sonstige Zuschläge.',
      },
      {
        label: t('Time fee') + ' in €',
        key: 'timeFee',
        type: 'number',
      },
      {
        label: t('Special Services') + ' in €',
        key: 'specialServices',
        type: 'number',
      },
      {
        label: t('Additional Services') + ' in €',
        key: 'additionalServices',
        type: 'number',
      },
      {
        label: t('Incidental Costs') + ' in %',
        key: 'incidentalCosts',
        type: 'number',
      },
      /*{
        label: t('Building substance to be processed') + ' in %',
        key: 'buildingSubstance',
        type: 'number',
      },
      {
        label: t('Share of TGA (technical building equipment)') + ' in %',
        key: 'tgaShare',
        type: 'number',
      },*/
      /*{
        label: t('VAT') + ' in %',
        key: 'vat',
        type: 'number',
      },*/
    ],
  };

  function getLabelIndexByLabel(hoaiVersion, label) {
    if (!label || !hoaiVersion) return 0;
    const index = hoaiVersion.data.findIndex((d) => d.label === label);
    return index >= 0 ? index : 0;
  }

  function mapResponseToDataCollection(res) {
    const hoaiIndex = getHoaiIndexByYear(res.year ?? res.hoaiData?.year);
    const hoaiVersion = hoais[hoaiIndex];
    const labelIndex =
      res.hoaiData?.labelIndex ?? getLabelIndexByLabel(hoaiVersion, res.label);

    return {
      labelIndex: labelIndex,
      year: res.year ?? res.hoaiData?.year,
      baseAmount: res.base_amount ?? res.baseAmount ?? res.hoaiData?.baseAmount,
      honorariumZone:
        res.honorarium_zone ??
        res.honorariumZone ??
        res.hoaiData?.honorariumZone,
      honorariumRate:
        res.honorarium_rate ??
        res.honorariumRate ??
        res.hoaiData?.honorariumRate ??
        0,
      baseHonorarium:
        res.baseHonorarium ??
        res.base_honorarium ??
        res.hoaiData?.baseHonorarium,
      nettoHonorarium:
        res.nettoHonorarium ??
        res.netto_honorarium ??
        res.hoaiData?.nettoHonorarium,
      resultingHonorarium:
        res.nettoHonorarium ??
        res.netto_honorarium ??
        res.hoaiData?.resultingHonorarium,
      surcharge: res.surcharge ?? res.hoaiData?.surcharge,
      surchargeTitle: res.surchargeTitle ?? res.hoaiData?.surchargeTitle,
      surchargeDescription:
        res.surchargeDescription ?? res.hoaiData?.surchargeDescription,
      timeFee: res.time_fee ?? res.timeFee ?? res.hoaiData?.timeFee,
      timeFeeTitle:
        res.timeFeeTitle ?? res.time_fee_title ?? res.hoaiData?.timeFeeTitle,
      timeFeeDescription:
        res.timeFeeDescription ??
        res.time_fee_description ??
        res.hoaiData?.timeFeeDescription,
      specialServices:
        res.special_services ??
        res.specialServices ??
        res.hoaiData?.specialServices,
      specialServicesTitle:
        res.specialServicesTitle ??
        res.special_services_title ??
        res.hoaiData?.specialServicesTitle,
      specialServicesDescription:
        res.specialServicesDescription ??
        res.special_services_description ??
        res.hoaiData?.specialServicesDescription,
      additionalServices:
        res.additional_services ??
        res.additionalServices ??
        res.hoaiData?.additionalServices,
      additionalServicesTitle:
        res.additionalServicesTitle ??
        res.additional_services_title ??
        res.hoaiData?.additionalServicesTitle,
      additionalServicesDescription:
        res.additionalServicesDescription ??
        res.additional_services_description ??
        res.hoaiData?.additionalServicesDescription,
      incidentalCosts:
        res.incidental_costs ??
        res.incidentalCosts ??
        res.hoaiData?.incidentalCosts,
      incidentalCostsTitle:
        res.incidentalCostsTitle ??
        res.incidental_costs_title ??
        res.hoaiData?.incidentalCostsTitle,
      incidentalCostsDescription:
        res.incidentalCostsDescription ??
        res.incidental_costs_description ??
        res.hoaiData?.incidentalCostsDescription,
      vat: res.vat ?? res.hoaiData?.vat,
      label: res.label ?? res.hoaiData?.label,
      previousBaseHonorarium:
        res.previous_base_honorarium ??
        res.previousBaseHonorarium ??
        res.hoaiData?.previousBaseHonorarium,
      previousUpperHonorarium:
        res.previous_upper_honorarium ??
        res.previousUpperHonorarium ??
        res.hoaiData?.previousUpperHonorarium,
      nextBaseHonorarium:
        res.next_base_honorarium ??
        res.nextBaseHonorarium ??
        res.hoaiData?.nextBaseHonorarium,
      nextUpperHonorarium:
        res.next_upper_honorarium ??
        res.nextUpperHonorarium ??
        res.hoaiData?.nextUpperHonorarium,
      prevTableHonorarium:
        res.prev_table_honorarium ??
        res.prevTableHonorarium ??
        res.hoaiData?.prevTableHonorarium,
      nextTableHonorarium:
        res.next_table_honorarium ??
        res.nextTableHonorarium ??
        res.hoaiData?.nextTableHonorarium,
      dinId: res.din?.id ?? res.hoaiData?.dinId,
      showSiemon:
        res.hoaiData?.showSiemon ??
        (res.positions?.some(
          (p) => p.siemonPositions && p.siemonPositions.length > 0,
        ) ||
          false),
    };
  }

  function parsePhasesFromPositions(positions, staticPhases) {
    if (!positions || positions.length === 0) return null;

    // If we have static phases, use them as base and merge position data on top
    // This ensures disabled/missing phases are still represented
    if (staticPhases && staticPhases.length > 0) {
      return staticPhases.map((staticPhase, index) => {
        // Match by index — positions are in the same order as static phases
        const pos = positions[index];

        if (!pos || Boolean(pos.disabled)) {
          // Phase not in positions or explicitly disabled
          return {
            title: `${index + 1}. ${staticPhase.title}`,
            maxPercent: staticPhase.maxPercent,
            disabled: true,
            siemon: staticPhase.siemon,
          };
        }

        const phase = {
          id: pos.id,
          title: pos.title,
          maxPercent: pos.maxPercent ?? staticPhase.maxPercent,
          value: pos.value != null ? Number(pos.value) : undefined,
          flatValue:
            pos.type === 'flat'
              ? Number(pos.phaseHonorarium ?? pos.phase_honorarium) || 0
              : undefined,
          phaseHonorarium: pos.phaseHonorarium
            ? Number(pos.phaseHonorarium)
            : undefined,
          description: pos.description,
          type: pos.type || 'default',
          disabled: Boolean(pos.disabled),
        };

        if (pos.siemonPositions && pos.siemonPositions.length > 0) {
          phase.siemon = pos.siemonPositions.map((s) => ({
            id: s.id,
            title: s.title,
            maxPercent: s.maxPercent,
            siemonValue: s.value != null ? Number(s.value) : undefined,
            phaseHonorarium: s.phaseHonorarium
              ? Number(s.phaseHonorarium)
              : undefined,
            siemonDescription: s.description,
            disabled: Boolean(s.disabled),
          }));
        } else if (staticPhase.siemon) {
          phase.siemon = staticPhase.siemon;
        }

        return phase;
      });
    }

    // No static phases available — use positions as-is
    return positions.map((pos) => {
      const phase = {
        id: pos.id,
        title: pos.title,
        maxPercent: pos.maxPercent,
        value: pos.value != null ? Number(pos.value) : undefined,
        flatValue: pos.flatValue ?? pos.flat_value,
        phaseHonorarium: pos.phaseHonorarium
          ? Number(pos.phaseHonorarium)
          : undefined,
        description: pos.description,
        type: pos.type || 'default',
        disabled: Boolean(pos.disabled),
      };

      if (pos.siemonPositions && pos.siemonPositions.length > 0) {
        phase.siemon = pos.siemonPositions.map((s) => ({
          id: s.id,
          title: s.title,
          maxPercent: s.maxPercent,
          siemonValue: s.value != null ? Number(s.value) : undefined,
          phaseHonorarium: s.phaseHonorarium
            ? Number(s.phaseHonorarium)
            : undefined,
          siemonDescription: s.description,
          disabled: Boolean(s.disabled),
        }));
      }

      return phase;
    });
  }

  function generateRateOptions(hoai) {
    const options = hoai.rateOptions.map((el) => {
      return { label: t(el.label), value: el.value };
    });

    return options;
  }

  const [fields, setFields] = useState([
    {
      label: hoai.data[0].unitLabel,
      key: 'baseAmount',
      type: hoai.data[0].unit == 'euro' ? 'money' : 'number',
      colspan: 2,
    },
    {
      label: t('Honorarium Zone'),
      key: 'honorariumZone',
      type: 'customSelect',
      options: generateHonorariumZone(selectedLabel),
      noUnselect: true,
    },
    {
      label: t('Honorarium rate'),
      key: 'honorariumRate',
      type: 'numberSelect',
      noUnselect: true,
      options: generateRateOptions(hoai),
      unit: '%',
    },
    additionalCostField,
    {
      type: 'hoaiPhases',
      baseHonorariumKey: 'baseHonorarium',
      resultingHonorariumKey: 'resultingHonorarium',
      phases: hoai.data[0].phases,
      colspan: 2,
    },
  ]);

  function generateHOAIOptions() {
    return hoais.map((elem, index) => {
      return { label: elem.year, value: index };
    });
  }

  function generateLabelOptions() {
    return hoai.data.map((elem, index) => {
      return { label: elem.label, value: index };
    });
  }

  function generateHonorariumZone(selectedLabel) {
    const keys = Object.keys(hoai.data[selectedLabel].honorariumZone);
    return keys.map((label, index) => {
      return { label: label, value: index + 1 };
    });
  }

  function updateFields(selectedLabel, selectedHOAI, loadedDataCollection) {
    const hoai = hoais[selectedHOAI];

    const newFields = [
      {
        label: hoai.data[selectedLabel].unitLabel,
        key: 'baseAmount',
        type: hoai.data[selectedLabel].unit == 'euro' ? 'money' : 'number',
        colspan: 2,
      },
      {
        label: t('Honorarium Zone'),
        key: 'honorariumZone',
        type: 'customSelect',
        options: generateHonorariumZone(selectedLabel),
        noUnselect: true,
      },
      {
        label: t('Honorarium rate'),
        key: 'honorariumRate',
        type: 'numberSelect',
        noUnselect: true,
        options: generateRateOptions(hoai),
        unit: '%',
      },
      additionalCostField,
      {
        type: 'hoaiPhases',
        baseHonorariumKey: 'baseHonorarium',
        resultingHonorariumKey: 'resultingHonorarium',
        phases: hoai.data[selectedLabel].phases,
        colspan: 2,
      },
    ];

    if (loadedDataCollection) {
      setDataCollection({ ...loadedDataCollection, hoaiIndex: selectedHOAI });
      formRef.current.updateDataCollection({
        ...loadedDataCollection,
        hoaiIndex: selectedHOAI,
        year: hoais[selectedHOAI].year,
      });
    } else {
      setDataCollection({
        ...dataCollection,
        //phases: [],
        labelIndex: selectedLabel,
        hoaiIndex: selectedHOAI,
        year: hoais[selectedHOAI].year,
      });
      //formRef.current.updateDataCollection({
      //  //phases: [],
      //  hoaiIndex: selectedHOAI,
      //});
    }
    setHoai(hoai);
    setFields(newFields);
  }

  const hoaiVersionFields = {
    label: t('HOAI version'),
    key: 'hoaiIndex',
    options: generateHOAIOptions(),
    noUnselect: true,
  };

  const labelField = {
    label: '',
    key: 'labelIndex',
    options: generateLabelOptions(),
    noUnselect: true,
  };

  function handleHOAIChange(key, newHOAI) {
    handleLabelChange(null, 0, newHOAI);
  }

  function handleLabelChange(key, newLabel, newHOAI) {
    setSelectedLabel(newLabel);
    updateFields(
      newLabel,
      newHOAI ??
        dataCollection.hoaiIndex ??
        getHoaiIndexByYear(dataCollection.year) ??
        0,
      null,
    );

    calculateBaseHonorarium(dataCollection, null, newLabel, newHOAI);
  }

  const findBoundingValues = (
    honorariumZone,
    selectedHonorariumZone,
    value,
  ) => {
    const keys = Object.keys(honorariumZone[selectedHonorariumZone])
      .map(Number)
      .sort((a, b) => a - b);

    let previous = null;
    let next = null;

    for (let i = 0; i < keys.length; i++) {
      if (keys[i] == value) {
        if (i == 0) {
          previous = keys[i];
          next = keys[i + 1];
        } else {
          previous = keys[i - 1];
          next = keys[i];
        }
        break;
      }
      if (keys[i] < value) {
        previous = keys[i];
      }
      if (keys[i] > value) {
        next = keys[i];
        break;
      }
    }

    return { previous, next };
  };

  function calculateBaseHonorarium(data, hoaiIndex, newSelectedLabel, newHOAI) {
    const localHOAI = newHOAI || newHOAI == 0 ? hoais[newHOAI] : hoai;
    const localSelectedLabel = newSelectedLabel ?? selectedLabel;
    data = { ...data, hoaiIndex: hoaiIndex ?? dataCollection.hoaiIndex };

    if (data.honorariumRate > 100) {
      return formRef.current.updateDataCollection({
        honorariumRate: 100,
      });
    }
    if (data.honorariumRate < 0) {
      return formRef.current.updateDataCollection({
        honorariumRate: 0,
      });
    }

    setTooLowWarning(false);
    setPhasesOver100(false);

    var selectedHonorariumZone = Object.keys(
      localHOAI.data[localSelectedLabel].honorariumZone,
    )[data.honorariumZone - 1];

    var realHonorariumZone = data.honorariumZone;

    //set honorarzone to 1 if the label changes
    if (
      !localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone]
    ) {
      selectedHonorariumZone = 'I';
      realHonorariumZone = 1;
      if (!data.baseAmount) {
        return formRef.current.updateDataCollection({
          honorariumZone: realHonorariumZone,
        });
      }
    }

    if (!data.baseAmount) return;

    let baseAmountValues = Object.keys(
      localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone],
    );

    baseAmountValues = baseAmountValues.sort((a, b) => Number(a) - Number(b));

    if (
      data.baseAmount > Number(baseAmountValues[baseAmountValues.length - 1])
    ) {
      return formRef.current.updateDataCollection({
        baseAmount: Number(baseAmountValues[baseAmountValues.length - 1]),
      });
    }

    let baseAmount = data.baseAmount;

    if (data.baseAmount < Number(baseAmountValues[0])) {
      setTooLowWarning(true);
      baseAmount = 0;
    }

    const phases =
      data.phases && data.phases.length > 0
        ? data.phases
        : localHOAI.data[localSelectedLabel].phases;

    // All phases (including flat) contribute their percentage to the total
    const totalPhasePercent = phases.reduce(
      (sum, phase) =>
        phase.disabled !== true
          ? sum +
            (phase.value !== undefined
              ? Number(phase.value)
              : Number(phase.maxPercent))
          : sum,
      0,
    );

    // Flat phases contribute their fixed netto value instead of percentage-based calculation
    const totalFlatAmount = phases.reduce(
      (sum, phase) =>
        phase.disabled !== true && phase.type === 'flat'
          ? sum + (Number(phase.flatValue) || 0)
          : sum,
      0,
    );

    // Non-flat percentage total for the percentage-based honorarium calculation
    const normalPhasePercent = phases.reduce(
      (sum, phase) =>
        phase.disabled !== true && phase.type !== 'flat'
          ? sum +
            (phase.value !== undefined
              ? Number(phase.value)
              : Number(phase.maxPercent))
          : sum,
      0,
    );

    logResults && console.log('TotalPhasePercent: ', totalPhasePercent);

    if (totalPhasePercent > 100) {
      setPhasesOver100(true);
    }

    const honorariumRange = findBoundingValues(
      localHOAI.data[localSelectedLabel].honorariumZone,
      selectedHonorariumZone,
      Number(baseAmount),
    );

    // update the datacollection for out of range values
    if (!honorariumRange.previous || !honorariumRange.next) {
      setDataCollection({
        ...data,
        labelIndex: localSelectedLabel,
        baseHonorarium: 0,
        resultingHonorarium: 0,
        incidentalCostAmount: 0,
        vatAmount: 0,
        nettoHonorarium: 0,
        honorariumZone: realHonorariumZone,
      });

      formRef.current.updateDataCollection({
        baseHonorarium: 0,
        resultingHonorarium: 0,
        incidentalCostAmount: 0,
        vatAmount: 0,
        nettoHonorarium: 0,
        honorariumZone: realHonorariumZone,
      });

      return;
    }

    const prevTableHonorarium = honorariumRange.previous;
    const nextTableHonorarium = honorariumRange.next;

    const previousBaseHonorarium =
      localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone][
        honorariumRange.previous
      ].min;
    const previousUpperHonorarium =
      localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone][
        honorariumRange.previous
      ].max;
    const nextBaseHonorarium =
      localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone][
        honorariumRange.next
      ].min;

    const nextUpperHonorarium =
      localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone][
        honorariumRange.next
      ].max;

    const interpolatedLowerHonorarium =
      previousBaseHonorarium +
      ((baseAmount - honorariumRange.previous) *
        (nextBaseHonorarium - previousBaseHonorarium)) /
        (honorariumRange.next - honorariumRange.previous);

    const interpolatedUpperHonorarium =
      previousUpperHonorarium +
      ((baseAmount - honorariumRange.previous) *
        (nextUpperHonorarium - previousUpperHonorarium)) /
        (honorariumRange.next - honorariumRange.previous);

    const baseHonorarium =
      Math.round(
        100 *
          (interpolatedLowerHonorarium +
            ((interpolatedUpperHonorarium - interpolatedLowerHonorarium) *
              data.honorariumRate) /
              100),
      ) / 100;

    logResults && console.log('BaseAmount: ', baseAmount);

    logResults && console.log('HonorariumRange: ', honorariumRange);

    logResults &&
      console.log(
        'InterpolatedLowerBaseHonorarium: ',
        interpolatedLowerHonorarium,
      );

    logResults &&
      console.log(
        'InterpolatedUpperBaseHonorarium: ',
        interpolatedUpperHonorarium,
      );

    logResults && console.log('Base Honorarium: ', baseHonorarium);

    let resultingHonorarium =
      Math.round((100 * baseHonorarium * normalPhasePercent) / 100) / 100 +
      totalFlatAmount;

    logResults &&
      console.log('ResultingHonorariumAfterPhases: ', resultingHonorarium);

    //Zuschlag
    const surchargeAmount = data.surcharge
      ? Math.round(100 * resultingHonorarium * (Number(data.surcharge) / 100)) /
        100
      : '';

    resultingHonorarium = data.surcharge
      ? Math.round(
          100 * resultingHonorarium * (Number(data.surcharge) / 100 + 1),
        ) / 100
      : resultingHonorarium;

    logResults &&
      console.log('ResultingHonorariumAfterSurcharge: ', resultingHonorarium);

    //Zeithonorar
    resultingHonorarium = data.timeFee
      ? resultingHonorarium + Number(data.timeFee)
      : resultingHonorarium;

    logResults &&
      console.log('ResultingHonorariumAfterTimeFee: ', resultingHonorarium);

    //Besondere Leistungen
    resultingHonorarium = data.specialServices
      ? resultingHonorarium + Number(data.specialServices)
      : resultingHonorarium;

    logResults &&
      console.log(
        'ResultingHonorariumAfterSpecialServices: ',
        resultingHonorarium,
      );

    //Zusätzliche Leistungen
    resultingHonorarium = data.additionalServices
      ? resultingHonorarium + Number(data.additionalServices)
      : resultingHonorarium;

    logResults &&
      console.log(
        'ResultingHonorariumAfterAdditionalServices: ',
        resultingHonorarium,
      );

    //Nebenkosten
    const incidentalCostAmount = data.incidentalCosts
      ? Math.round(
          100 * resultingHonorarium * (Number(data.incidentalCosts) / 100),
        ) / 100
      : '';

    resultingHonorarium = data.incidentalCosts
      ? Math.round(
          100 * resultingHonorarium * (Number(data.incidentalCosts) / 100 + 1),
        ) / 100
      : resultingHonorarium;

    logResults &&
      console.log(
        'ResultingHonorariumAfterIncidentalCosts: ',
        resultingHonorarium,
      );

    /*const buildingSubstanceAmount = data.buildingSubstance
      ? Math.round(
          100 * resultingHonorarium * (Number(data.buildingSubstance) / 100)
        ) / 100
      : '';*/

    resultingHonorarium = data.buildingSubstance
      ? Math.round(
          100 *
            resultingHonorarium *
            (Number(data.buildingSubstance) / 100 + 1),
        ) / 100
      : resultingHonorarium;

    /*const tgaShareAmount = data.tgaShare
      ? Math.round(100 * resultingHonorarium * (Number(data.tgaShare) / 100)) /
        100
      : '';*/

    resultingHonorarium = data.tgaShare
      ? Math.round(
          100 * resultingHonorarium * (Number(data.tgaShare) / 100 + 1),
        ) / 100
      : resultingHonorarium;

    const nettoHonorarium = resultingHonorarium;

    //Mehrwertsteuer
    const vat = data.vat;
    const vatAmount = vat
      ? Math.round(100 * resultingHonorarium * (Number(vat) / 100)) / 100
      : '';

    resultingHonorarium = vat
      ? Math.round(100 * resultingHonorarium * (Number(vat) / 100 + 1)) / 100
      : resultingHonorarium;

    logResults &&
      console.log('ResultingHonorariumAfterVat: ', resultingHonorarium);
    logResults && console.log('--------------');

    logResults &&
      console.log('FinalResultingHonorarium: ', resultingHonorarium);

    const newPhases = phases.map((phase) => {
      return {
        ...phase,
        phaseHonorarium:
          phase.type === 'flat'
            ? Number(phase.flatValue) || 0
            : Math.round(
                (100 *
                  baseHonorarium *
                  Number(phase.value ?? phase.maxPercent)) /
                  100,
              ) / 100,
      };
    });

    if (
      data.resultingHonorarium != resultingHonorarium ||
      data.showSiemon != dataCollection.showSiemon
    ) {
      setDataCollection({
        ...data,
        label: localHOAI.data[localSelectedLabel].label,
        labelIndex: localSelectedLabel,
        baseHonorarium: baseHonorarium,
        resultingHonorarium: resultingHonorarium,
        surchargeAmount: surchargeAmount,
        timeFee: data.timeFee,
        specialServices: data.specialServices,
        additionalServices: data.additionalServices,
        incidentalCostAmount: incidentalCostAmount,
        //buildingSubstance: data.buildingSubstance,
        //buildingSubstanceAmount: buildingSubstanceAmount,
        //tgaShareAmount: tgaShareAmount,
        vat: vat,
        vatAmount: vatAmount,
        nettoHonorarium: nettoHonorarium,
        honorariumZone: realHonorariumZone,
        phases: newPhases,
        previousBaseHonorarium: previousBaseHonorarium,
        previousUpperHonorarium: previousUpperHonorarium,
        nextBaseHonorarium: nextBaseHonorarium,
        nextUpperHonorarium: nextUpperHonorarium,
        prevTableHonorarium: prevTableHonorarium,
        nextTableHonorarium: nextTableHonorarium,
        showSiemon: data.showSiemon,
      });

      formRef.current.updateDataCollection({
        label: localHOAI.data[localSelectedLabel].label,
        baseHonorarium: baseHonorarium,
        resultingHonorarium: resultingHonorarium,
        surchargeAmount: surchargeAmount,
        timeFee: data.timeFee,
        specialServices: data.specialServices,
        additionalServices: data.additionalServices,
        incidentalCostAmount: incidentalCostAmount,
        //buildingSubstance: data.buildingSubstance,
        //buildingSubstanceAmount: buildingSubstanceAmount,
        //tgaShareAmount: tgaShareAmount,
        vat: vat,
        vatAmount: vatAmount,
        nettoHonorarium: nettoHonorarium,
        honorariumZone: realHonorariumZone,
        phases: newPhases,
        //showSiemon: data.showSiemon ?? false,
      });
    }
  }

  useEffect(() => {
    if (debounceData) {
      calculateBaseHonorarium(debounceData);
    }
  }, [
    debounceCalc?.baseAmount,
    debounceCalc?.honorariumRate,
    debounceCalc?.honorariumZone,
    debounceCalc?.surcharge,
    debounceCalc?.timeFee,
    debounceCalc?.specialServices,
    debounceCalc?.additionalServices,
    debounceCalc?.incidentalCosts,
    //debounceCalc?.buildingSubstance,
    //debounceCalc?.tgaShare,
    debounceCalc?.vat,
    debounceCalc?.phases,
    debounceCalc?.showSiemon,
  ]);

  function handleDIN(res) {
    setDataCollection({
      ...dataCollection,
      baseAmount: res.baseAmount,
      dinData: res.dinData,
      dinTitle: res.dinTitle,
      dinId: res.dinId,
      dinYear: res.year,
    });
    formRef.current.updateDataCollection({
      baseAmount: res.baseAmount,
      dinData: res.dinData,
      dinTitle: res.dinTitle,
      dinId: res.dinId,
      dinYear: res.year,
    });

    calculateBaseHonorarium(formRef.current.dataCollection);
  }

  function handleSubmit() {
    const positions = dataCollection.phases
      ? dataCollection.phases.map((phase, index) => ({
          ...(phase.id != null && { id: phase.id }),
          title: phase.title,
          value: phase.value != null ? Number(phase.value) : undefined,
          max_percent: Number(phase.maxPercent),
          phase_honorarium:
            phase.type === 'flat'
              ? Number(phase.flatValue) || 0
              : phase.phaseHonorarium
                ? Number(phase.phaseHonorarium)
                : undefined,
          description: phase.description,
          type: phase.type || 'default',
          disabled: phase.disabled || false,
          phase_number: index + 1,
          ...(dataCollection.showSiemon &&
            phase.siemon?.length > 0 && {
              siemon_positions: phase.siemon.map((s, sIndex) => ({
                ...(s.id != null && { id: s.id }),
                title: s.title,
                value:
                  s.siemonValue != null ? Number(s.siemonValue) : undefined,
                max_percent: Number(s.maxPercent),
                phase_honorarium: s.phaseHonorarium
                  ? Number(s.phaseHonorarium)
                  : undefined,
                description: s.siemonDescription,
                disabled: s.disabled || false,
                phase_number: sIndex + 1,
              })),
            }),
        }))
      : [];

    const data = {
      title: titleCollection.title,
      base_honorarium: dataCollection.baseHonorarium,
      netto_honorarium: dataCollection.nettoHonorarium,
      positions: positions,
      year: dataCollection.year,
      valid_from: hoai.validFrom,
      base_amount_unit: hoai.data[selectedLabel].unit,
      label: dataCollection.label,
      base_amount: dataCollection.baseAmount,
      honorarium_zone: dataCollection.honorariumZone,
      honorarium_rate: dataCollection.honorariumRate,
      previous_base_honorarium: dataCollection.previousBaseHonorarium,
      previous_upper_honorarium: dataCollection.previousUpperHonorarium,
      next_base_honorarium: dataCollection.nextBaseHonorarium,
      next_upper_honorarium: dataCollection.nextUpperHonorarium,
      prev_table_honorarium: dataCollection.prevTableHonorarium,
      next_table_honorarium: dataCollection.nextTableHonorarium,
      surcharge: dataCollection.surcharge,
      surcharge_title: dataCollection.surchargeTitle || t('Surcharge'),
      surcharge_description: dataCollection.surchargeDescription,
      time_fee: dataCollection.timeFee,
      time_fee_title: dataCollection.timeFeeTitle || t('Time fee'),
      time_fee_description: dataCollection.timeFeeDescription,
      special_services: dataCollection.specialServices,
      special_services_title:
        dataCollection.specialServicesTitle || t('Special Services'),
      special_services_description: dataCollection.specialServicesDescription,
      additional_services: dataCollection.additionalServices,
      additional_services_title:
        dataCollection.additionalServicesTitle || t('Additional Services'),
      additional_services_description:
        dataCollection.additionalServicesDescription,
      incidental_costs: dataCollection.incidentalCosts,
      incidental_costs_title:
        dataCollection.incidentalCostsTitle || t('Incidental Costs'),
      incidental_costs_description: dataCollection.incidentalCostsDescription,
      vat: dataCollection.vat,
    };

    DBRequest({
      config,
      path: dataCollection?.id ? `hoai/${dataCollection.id}` : `hoai`,
      method: dataCollection?.id ? API_PATCH(data) : API_POST(data),
      onResponse: handleResponse,
    });
  }

  function handleResponse(res) {
    setDataCollection({ id: res.data.id, ...dataCollection });
    onSubmit({
      ...res.data,
      phases: dataCollection.phases,
      showSiemon: dataCollection.showSiemon,
      hoaiTitle: res.data.title,
      oldHoaiId: startDataCollection?.id,
    });

    onClose();
  }

  function handleTitleChange(key, value) {
    setTitleCollection({ title: value });
  }

  function setLoadedHOAI(data) {
    setOpenLoad(false);
    const res = data.data;
    setTitleCollection({ title: res.title });

    const mapped = mapResponseToDataCollection(res);
    const hoaiIndex = getHoaiIndexByYear(mapped.year);
    setSelectedLabel(mapped.labelIndex);

    const staticPhases = hoais[hoaiIndex].data[mapped.labelIndex].phases;
    const parsedPhases = parsePhasesFromPositions(res.positions, staticPhases);

    const initData = {
      ...mapped,
      id: res.id,
      hoaiIndex: hoaiIndex,
      phases: parsedPhases || res.hoaiData?.phases || [],
    };

    setStartData(initData);
    updateFields(mapped.labelIndex, hoaiIndex, initData);
    calculateBaseHonorarium(initData, hoaiIndex);
  }

  return (
    <Box>
      {debug && (
        <Button
          color="warning"
          variant="contained"
          onClick={() => console.log(dataCollection)}
          sx={{
            position: 'absolute',
            top: '0',
            left: '0',
          }}
        >
          LOG DATA
        </Button>
      )}
      <ExoDialog open={openDIN}>
        {openDIN && (
          <CostAllocator
            startData={dataCollection?.dinData}
            onClose={() => setOpenDIN(false)}
            onSubmit={handleDIN}
            dinTitle={dataCollection?.dinTitle}
            hoaiTitle={titleCollection.title}
            dinId={dataCollection?.dinId}
            startDinYear={dataCollection?.dinYear || hoai.correspondingDin}
            shownCostGroups={hoai.data[selectedLabel].shownCostGroups}
            disabledCostGroups={hoai.data[selectedLabel].disabledCostGroups}
            additionalFields={hoai.data[selectedLabel].additionalFields}
            creditableCostFunction={
              hoai.data[selectedLabel].creditableCostFunction
            }
          />
        )}
      </ExoDialog>
      <ExoDialog open={openLoad} onClose={() => setOpenLoad(false)}>
        <HOAILoader onSelect={setLoadedHOAI} />
      </ExoDialog>
      <Box className="flex justify-between">
        <Typography fontSize={24} fontWeight={500}>
          {t('HOAI Calculator')}
        </Typography>
        <IconButton onClick={() => setOpenLoad(true)}>
          <CloudDownloadIcon />
        </IconButton>
      </Box>
      <Box className="flex flex-col md:grid md:grid-cols-3 gap-4 my-3 mt-6">
        <TypeCustomSelect
          field={hoaiVersionFields}
          dataCollection={dataCollection}
          startDataCollection={startData}
          errors={{}}
          updateValidationOnChange={handleHOAIChange}
        />
        <Box className="w-full flex gap-4 md:col-span-2">
          <TypeCustomSelect
            field={labelField}
            dataCollection={dataCollection}
            startDataCollection={startData}
            errors={{}}
            updateValidationOnChange={handleLabelChange}
          />
          <Button
            variant="outlined"
            //color={'secondary'}
            onClick={() => setOpenDIN(true)}
            disabled={!hoai.data[selectedLabel].shownCostGroups}
            disableElevation
            sx={{
              backgroundColor: /*noBackground ? 'unset' :*/ colors.bgInput,
              ':hover': {
                cursor: /*onClick ? 'pointer' :*/ 'auto',
                border:
                  /*label
                  ? 'unset'
                  : */ '1px solid ' + colors.info + '80!important',
              },
              'fieldset:hover': {
                border: '1px solid ' + colors.info + '80!important',
              },
              border: /*label ? 'unset' :*/ `1px solid ${colors.selected}`,
            }}
          >
            {t('Creditable Costs from DIN 276')}
          </Button>
        </Box>
      </Box>
      <ExoForm
        debug={debug}
        outerClassName="h-fit"
        className="flex flex-col w-full md:grid md:grid-cols-2 gap-4"
        ref={formRef}
        fields={fields}
        noControls={true}
        startDataCollection={startData}
        onChange={setDebounceData}
      />

      {Boolean(dataCollection.surchargeAmount) && (
        <AdditionalCostLine
          label={t('Surcharge')}
          amount={dataCollection.surchargeAmount}
          percent={dataCollection.surcharge}
        />
      )}
      {Boolean(dataCollection.timeFee) && (
        <AdditionalCostLine
          label={t('Time fee')}
          amount={dataCollection.timeFee}
        />
      )}
      {Boolean(dataCollection.specialServices) && (
        <AdditionalCostLine
          label={t('Special Services')}
          amount={dataCollection.specialServices}
        />
      )}
      {Boolean(dataCollection.additionalServices) && (
        <AdditionalCostLine
          label={t('Additional Services')}
          amount={dataCollection.additionalServices}
        />
      )}
      {Boolean(dataCollection.incidentalCostAmount) && (
        <AdditionalCostLine
          label={t('Incidental Costs')}
          amount={dataCollection.incidentalCostAmount}
          percent={dataCollection.incidentalCosts}
        />
      )}
      {Boolean(dataCollection.buildingSubstanceAmount) && (
        <AdditionalCostLine
          label={t('Building substance to be processed')}
          amount={dataCollection.buildingSubstanceAmount}
          percent={dataCollection.buildingSubstance}
        />
      )}
      {Boolean(dataCollection.tgaShareAmount) && (
        <AdditionalCostLine
          label={t('Share of TGA')}
          amount={dataCollection.tgaShareAmount}
          percent={dataCollection.tgaShare}
        />
      )}
      <Divider />
      <Box className="flex justify-between pr-3 pl-2 mb-3 mt-3">
        <Typography fontWeight={600}>
          {t('Netto Honorarium including additional Costs')}
        </Typography>
        <Typography fontWeight={600}>
          <MoneyLabel money={dataCollection.nettoHonorarium} />
        </Typography>
      </Box>

      {/*Boolean(dataCollection.vatAmount) && (
        <AdditionalCostLine
          label={t('VAT')}
          amount={dataCollection.vatAmount}
          percent={dataCollection.vat}
        />
      )*/}
      <Box className="flex justify-between items-center">
        <Box className="w-[125px]">
          <FormFields
            dataCollection={dataCollection}
            updateDataCollection={(data) => {
              const newDataCollection = { ...dataCollection, vat: data.vat };
              setDataCollection(newDataCollection);
              calculateBaseHonorarium(newDataCollection);
            }}
            fields={[
              {
                size: 'small',
                label: t('VAT'),
                key: 'vat',
                type: 'number',
                unit: '%',
              },
            ]}
          />
        </Box>
        <Box className="flex justify-end pl-2 pr-3 mb-2">
          <Typography
            className="text-right"
            sx={{ width: '125px', minWidth: '125px' }}
          >
            <MoneyLabel money={dataCollection?.vatAmount ?? 0} />
          </Typography>
        </Box>
      </Box>
      <Box className="h-3" />
      <Divider />
      {tooLowWarning && (
        <Alert severity="error">
          {t('The given ') +
            hoai.data[selectedLabel].unitLabel +
            t(' are too low')}
        </Alert>
      )}
      {phasesOver100 && (
        <Alert severity="warning">{t('The phases surpass 100%')}</Alert>
      )}
      <Box className="flex justify-between pr-3 pl-2 mb-1 mt-5">
        <Typography fontWeight={600}>
          {t('Brutto Honorarium including additional Costs')}
        </Typography>
        <Typography fontWeight={600}>
          <MoneyLabel money={dataCollection.resultingHonorarium} />
        </Typography>
      </Box>
      <Box className="flex justify-end w-full px-2 pt-2">
        <Typography>{t('All results are without guarantee')}</Typography>
      </Box>
      {!noControls && (
        <Box className="flex justify-between py-5">
          <Button variant="contained" color="error" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Box className="flex items-center gap-2">
            <TypeText
              size="small"
              field={{ label: t('Save as'), key: 'title' }}
              dataCollection={titleCollection}
              errors={{}}
              updateValidationOnChange={handleTitleChange}
            />
            <Button
              className="h-full"
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={
                !titleCollection.title ||
                Object.keys(dataCollection).length == 0 ||
                !dataCollection.baseHonorarium ||
                !dataCollection.baseAmount ||
                dataCollection.baseHonorarium == 0 ||
                dataCollection.baseAmount == 0 ||
                dataCollection.vat === null ||
                dataCollection.vat === undefined
              }
            >
              {t('Save')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const AdditionalCostLine = ({ label, amount, percent }) => {
  return (
    <Box className="flex justify-end pl-2 pr-3 mb-2">
      <Typography className="text-left w-full">
        {`${label} ${percent ? `(${percent}%)` : ''}`}
      </Typography>
      <Typography
        className="text-right"
        sx={{ width: '125px', minWidth: '125px' }}
      >
        <MoneyLabel money={amount} />
      </Typography>
    </Box>
  );
};
